// Shared mapping from the parsed `ChatTableData` model to the
// `/v1/artifacts/generate` wire shape. Both the per-table toolbar and the
// thread-level "All Tables" export build their payloads through here, so the
// single-table and merged exports stay byte-for-byte identical.
import type { IArtifactCell, IArtifactTable } from "@/api/artifacts";
import type { ICitationResolution } from "@/api/chat/chat.types";

import type { CellSpec, ChatTableData } from "./parse-markdown-table";

/** Resolves an opaque `cite://cN` handle to the document/page it cites, or
 * `undefined` when the handle is unknown. Native citation cells carry only a
 * handle; the backend hyperlink needs the document_id/page this supplies. */
export type ResolveCitationHandle = (
    handle: string
) => { document_id: number; page?: number } | undefined;

/** Build a {@link ResolveCitationHandle} from a message's `citation_resolutions`
 * (the handle -> resolution list set live by the `citation_resolved` stream
 * event and rehydrated on reload). Null pages are dropped so the wire cell
 * omits `page` rather than sending `null`. */
export function citationHandleResolver(
    resolutions: ICitationResolution[] | undefined
): ResolveCitationHandle {
    const byHandle = new Map((resolutions ?? []).map((r) => [r.handle, r]));
    return (handle) => {
        const r = byHandle.get(handle);
        if (!r) return undefined;
        return r.page != null
            ? { document_id: r.document_id, page: r.page }
            : { document_id: r.document_id };
    };
}

// Characters Excel forbids in a worksheet name. The backend schema
// *rejects* (422) any caption containing one of these; see
// `EXCEL_INVALID_SHEET_NAME_CHARS` in `api/app/schemas/v1/artifact.py`,
// the source of truth. Mirrored here only to strip them client-side so a
// sanitized caption clears that gate; the list is an Excel/xlsxwriter
// invariant, so this physical-constant duplication is low-fragility.
const EXCEL_INVALID_SHEET_NAME_CHARS = ["\\", "/", "?", "*", "[", "]", ":"];
// xlsxwriter's hard sheet-name length limit.
const MAX_SHEET_NAME_LENGTH = 31;
// Backend `ArtifactTable.title` is `Field(max_length=200)` and *rejects*
// (422) past it, same reject-not-truncate gate as `caption`. One over-long
// heading would otherwise fail the entire merged export, so clamp here.
const MAX_TABLE_TITLE_LENGTH = 200;

/**
 * Lift a parsed cell to the wire shape. When the cell carries source
 * refs, promote the first citation's `document_id` / `page` to the top
 * level so the backend can emit a real Excel hyperlink; otherwise send
 * plain text. The wire shape carries one hyperlink per cell, so a
 * multi-citation cell anchors on its first source.
 *
 * A native `cite://cN` source holds only an opaque handle, so it needs
 * `resolveHandle` (the per-message citation map) to recover document_id/page.
 * An unresolved handle degrades to plain text rather than a broken link.
 */
export function toArtifactCell(
    cell: CellSpec,
    resolveHandle?: ResolveCitationHandle
): IArtifactCell {
    const source = cell.sources?.[0];
    if (!source) return { text: cell.text };

    if (source.handle !== undefined) {
        const resolved = resolveHandle?.(source.handle);
        if (!resolved) return { text: cell.text };
        const out: IArtifactCell = {
            text: cell.text,
            document_id: resolved.document_id,
        };
        if (resolved.page !== undefined) out.page = resolved.page;
        return out;
    }

    // Legacy `link://source` cell: document_id/page are already inline.
    const out: IArtifactCell = {
        text: cell.text,
        document_id: source.document_id,
    };
    if (source.page !== undefined) out.page = source.page;
    return out;
}

/**
 * Minimal caption sanitizer: strip Excel-invalid characters and clamp to
 * 31 chars, just enough to clear the backend's reject-not-truncate schema
 * gate. Returns `undefined` when nothing usable survives (so the backend
 * falls back to `Sheet N` rather than receiving an empty caption). The
 * generator's `_sheet_name` still owns truncation-for-quality and
 * cross-sheet de-duplication, so this deliberately does neither.
 */
export function toSheetCaption(title?: string): string | undefined {
    if (!title) return undefined;
    let cleaned = title;
    for (const ch of EXCEL_INVALID_SHEET_NAME_CHARS) {
        cleaned = cleaned.split(ch).join("");
    }
    cleaned = cleaned.slice(0, MAX_SHEET_NAME_LENGTH).trim();
    return cleaned.length > 0 ? cleaned : undefined;
}

/** Map a parsed table to the `/v1/artifacts/generate` table payload.
 * `resolveHandle` resolves native cite:// cells against the source message's
 * citation map (see {@link citationHandleResolver}). */
export function chatTableDataToArtifactTable(
    data: ChatTableData,
    resolveHandle?: ResolveCitationHandle
): IArtifactTable {
    // `title` is the in-sheet body heading; `caption` is its truncated/
    // sanitized projection for the 31-char worksheet tab (via toSheetCaption).
    // The heading clamps to the backend's 200-char title cap so a single
    // long heading can't 422 the whole merged export.
    const title = data.title?.trim().slice(0, MAX_TABLE_TITLE_LENGTH);
    const caption = toSheetCaption(data.title);
    return {
        headers: data.headers.map((h) => h.text),
        rows: data.rows.map((row) =>
            row.map((c) => toArtifactCell(c, resolveHandle))
        ),
        ...(title ? { title } : {}),
        ...(caption ? { caption } : {}),
    };
}
